<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';

import {
  Authenticator,
  AuthenticatorForceNewPasswordFormFields,
  ForceNewPassword,
  translations,
  useAuthenticator,
} from '@aws-amplify/ui-vue';
import { signInWithRedirect } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import { useI18n } from 'vue-i18n';

import { useErrorHandling } from '@/composables/error';

import type { User } from '@/models/user';
import type { Permission } from '@/models/user-role';

import type { RouteName } from '@/router/routes';

import {
  amplifyAuthService,
  configureAmplifyFromTenantConfig,
  hasCustomerOidcClientId,
  resetToNativeClient,
} from '@/services/auth';
import { billingService } from '@/services/billing';
import { userService } from '@/services/user';

import { seatAllocationStore } from '@/store/seat-allocation';
import { userStore } from '@/store/user';

import { delay } from '@/utility/delay';

// Only reset to native client if this is NOT an OIDC callback
// OIDC callbacks have 'code' and 'state' params in the URL
const urlParams = new URLSearchParams(globalThis.location.search);
const isOidcCallback = urlParams.has('code');
if (!isOidcCallback) {
  resetToNativeClient();
}
await configureAmplifyFromTenantConfig();

const { t } = useI18n();

I18n.putVocabulariesForLanguage('de', {
  ...translations.de,
  'Change Password': t('loginView.changePassword'),
  'Reset Password': t('loginView.resetPassword'),
  'Enter your Email': t('loginView.enterEmail'),
  'Enter your Password': t('loginView.enterPassword'),
  'Your passwords must match': t('loginView.passwordMatch'),
  // Amplify's own client-side validator still rejects at 8 characters and emits this English message; the user pool enforces 12 (infra cognito-product.ts), so it is mapped to the 12-character hint.
  'Password must have at least 8 characters': t('loginView.passwordPolicy.length.long'),
  'Password does not conform to policy: Password not long enough': t('loginView.passwordPolicy.length.long'),
  'Password does not conform to policy: Password must have lowercase characters': t('loginView.passwordPolicy.lowercase.long'),
  'Password does not conform to policy: Password must have uppercase characters': t('loginView.passwordPolicy.uppercase.long'),
  'Password does not conform to policy: Password must have numeric characters': t('loginView.passwordPolicy.digit.long'),
  'Password does not conform to policy: Password must have symbol characters': t('loginView.passwordPolicy.symbol.long'),
  'Incorrect username or password.': t('loginView.invalidCredentials'),
});
I18n.setLanguage('de');

const formFields = {
  forgotPassword: {
    username: {
      label: t('loginView.resetPassword'),
    },
  },
};

const isLoggedIn = computed(() => userStore.loggedIn);

const { handleGetError } = useErrorHandling();
const router = useRouter();
const route = useRoute();
const auth = useAuthenticator();

type InitialLoginError = 'linkInvalid' | 'signInFailed';

interface InitialLoginCredentials {
  username: string;
  password: string;
}

const isInitialLogin = ref(false);
const initialLoginError = ref<InitialLoginError | null>(null);
const redirectingToSidebar = ref(false);

const heading = computed(() => (isInitialLogin.value
  ? t('loginView.initialLoginTitle')
  : t('loginView.title')));

const isProcessingInitialLogin = computed(() => !initialLoginError.value
  && (!!route.query.initial || (isInitialLogin.value && auth.route === 'signIn')));

const initialLoginErrorMessage = computed(() => (initialLoginError.value
  ? t(`loginView.initialLoginError.${initialLoginError.value}`)
  : ''));

const redirectMap: { [R in RouteName]?: Permission } = {
  Dashboard: 'canSeeDashboard',
  TargetManagement: 'canSeeTargets',
  RuleManagement: 'canSeeRules',
  ChannelManagement: 'canSeeChannels',
  UserManagement: 'canSeeUsers',
  CatalogManagement: 'canSeeCatalog',
};

const showKMHSupportButton = computed(() => 'support' in route.query);
const showCustomerOidcButton = computed(() => hasCustomerOidcClientId());

async function redirectToSidebar() {
  redirectingToSidebar.value = true;

  await delay(3000);

  const sidebarDomain = globalThis.location.origin.replace('.cockpit.', '.sidebar.');
  globalThis.location.assign(sidebarDomain);
}

/**
 * Shows the missing seat warning dialog
 * based on the available seats and the user's rights.
 */
function evaluateSeatWarningDialog(availableSeats: number, user: User) {
  const { seatAllocated } = user;
  const { canEditBilling, canEditUsers } = user.role;
  const hasBillingOrUserRight = canEditBilling || canEditUsers;
  const isMissingSeats = availableSeats < 0;

  if (isMissingSeats && hasBillingOrUserRight) {
    seatAllocationStore.warningDialog = {
      show: true,
      canEditUsers,
      canEditBilling,
      seatAllocated,
    };
  }
}

/**
 * Gets the next best allowed route to redirect the user to after login.
 */
function getRedirectRoute(user: User): RouteName {
  // eslint-disable-next-line no-restricted-syntax
  for (const [name, permission] of Object.entries(redirectMap)) {
    const routeName = name as RouteName;
    if (user.role[permission]) {
      return routeName;
    }
  }

  throw new Error('User does not have any rights');
}

/**
 * Reads the credentials an invitation link carries in its `initial` parameter.
 * The parameter is base64-encoded JSON written by the invitation mail (infra/packages/cognito-send-mail), so a truncated, re-encoded or hand-edited link fails to decode.
 * Returns null for anything that is not such a payload; the caller must then treat the link as no invitation at all.
 */
function decodeInitialCredentials(initial: string): InitialLoginCredentials | null {
  try {
    const { username, password } = JSON.parse(atob(initial)) as Partial<InitialLoginCredentials>;

    if (typeof username !== 'string' || typeof password !== 'string') {
      return null;
    }

    return { username, password };
  } catch {
    return null;
  }
}

/**
 * Fills the Amplify sign-in form with the invitation credentials and submits it.
 * Returns false when the form is not on the page, so the caller can fall back to the manual sign-in instead of waiting for a sign-in that never starts.
 */
function submitInitialLogin({ username, password }: InitialLoginCredentials): boolean {
  const usernameInput = document.querySelector<HTMLInputElement>('input[name="username"]');
  const passwordInput = document.querySelector<HTMLInputElement>('input[name="password"]');
  const signInButton = document.querySelector<HTMLButtonElement>('button[data-variation="primary"]');

  if (!usernameInput || !passwordInput || !signInButton) {
    return false;
  }

  usernameInput.value = username;
  passwordInput.value = password;
  signInButton.click();

  return true;
}

async function signInWithCustomerOIDC() {
  try {
    await amplifyAuthService.useCustomerOidcLoginConfig();
    const customerName = globalThis.location.hostname.split('.')[0];
    await signInWithRedirect({ provider: { custom: `${customerName}-OIDC` } });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Amplify][Cockpit] Customer OIDC login failed', e);
  }
}

async function signInAsKMHSupport() {
  try {
    await amplifyAuthService.useKMHOidcLoginConfig();
    await signInWithRedirect({ provider: { custom: 'KmhSupport' } });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Amplify][Cockpit] KMH Support login failed', e);
  }
}

watch(() => auth.authStatus, async () => {
  if (auth.authStatus === 'authenticated') {
    try {
      const {
        idToken,
        isInitialSSOLoginRequired,
        isInvalidSupportAccess,
      } = await amplifyAuthService.claimSSOUser();

      if (isInvalidSupportAccess) {
        auth.signOut();
        return;
      }

      if (isInitialSSOLoginRequired) {
        await userService.configureInitialSSOLogin(idToken);
      }

      const user = await userService.getCurrentUser();

      if (user.role.canUseCockpit) {
        Object.assign(userStore, { loggedIn: true, user });

        seatAllocationStore.availableSeats = await billingService.getAvailableSeats();
        evaluateSeatWarningDialog(seatAllocationStore.availableSeats, user);

        if (typeof route.query.redirect === 'string') {
          await router.push(route.query.redirect);
        } else {
          await router.push({
            name: getRedirectRoute(user),
          });
        }
      } else if (user.role.canUseSidebar) {
        await redirectToSidebar();
      } else {
        auth.signOut();
      }
    } catch (error) {
      handleGetError(error, 'users');
      auth.signOut();
    }
  }
}, { immediate: true });

watch(() => auth.route, async () => {
  if (auth.route !== 'signIn') {
    initialLoginError.value = null;
    return;
  }

  if (auth.authStatus === 'unauthenticated') {
    await nextTick();
    const { initial, ...otherParams } = route.query;

    if (initial) {
      const credentials = typeof initial === 'string' ? decodeInitialCredentials(initial) : null;

      isInitialLogin.value = !!credentials;
      initialLoginError.value = credentials ? null : 'linkInvalid';

      // remove initial query param from url
      await router.replace({
        query: otherParams,
      });

      if (credentials && !submitInitialLogin(credentials)) {
        initialLoginError.value = 'signInFailed';
      }
    }
  }
});

// A rejected invitation link leaves the authenticator on the sign-in route, so the placeholder has to be taken back explicitly or it would hide the error from the visitor forever.
watch(() => auth.error, () => {
  if (isInitialLogin.value && auth.route === 'signIn' && auth.error) {
    initialLoginError.value = 'signInFailed';
  }
});

watchEffect(() => {
  document.title = isInitialLogin.value
    ? t('loginView.initialLoginTitle')
    : `${t('loginView.title')} - intellivend`;
});

</script>

<template>
  <i-login-wrapper v-if="auth.authStatus !== 'authenticated'">
    <div class="w-full max-w-174 p-8 lg:px-12 2xl:mx-20">
      <!-- Sits outside the authenticator container so the visitor still reads it while the container is hidden behind the placeholder -->
      <i-message
        v-if="initialLoginError"
        data-test="initial-login-error"
        severity="error"
        :icon="farTriangleExclamation"
        class="mb-6"
      >
        {{ initialLoginErrorMessage }}
      </i-message>

      <div data-test="authenticator-container" :class="{ hidden: isProcessingInitialLogin }">
        <h1 class="hidden [@media(min-height:450px)]:block mb-6 lg:text-[2rem]">
          {{ heading }}
        </h1>

        <authenticator
          v-if="auth.authStatus !== 'authenticated'"
          hide-sign-up
          :login-mechanisms="['email']"
          :form-fields="formFields"
        >
          <template #force-new-password>
            <force-new-password>
              <template #force-new-password-form-fields>
                <authenticator-force-new-password-form-fields />

                <div class="mt-4">
                  <h3 class="amplify-heading amplify-heading--5">
                    {{ t('loginView.passwordPolicy.heading') }}
                  </h3>
                  <ul class="m-0 mt-2 px-5">
                    <li class="mb-1">
                      <span class="amplify-label">{{ t('loginView.passwordPolicy.length.short') }}</span>
                    </li>
                    <li class="mb-1">
                      <span class="amplify-label">{{ t('loginView.passwordPolicy.lowercase.short') }}</span>
                    </li>
                    <li class="mb-1">
                      <span class="amplify-label">{{ t('loginView.passwordPolicy.uppercase.short') }}</span>
                    </li>
                    <li class="mb-1">
                      <span class="amplify-label">{{ t('loginView.passwordPolicy.digit.short') }}</span>
                    </li>
                    <li>
                      <span class="amplify-label">{{ t('loginView.passwordPolicy.symbol.short') }}</span>
                    </li>
                  </ul>
                </div>
              </template>
            </force-new-password>
          </template>

          <!-- OIDC buttons should be in the authenticator not outside -->
          <template v-if="showCustomerOidcButton || showKMHSupportButton" #footer>
            <div class="flex flex-col gap-4">
              <i-button
                v-if="showCustomerOidcButton"
                data-test="customer-oidc-login-button"
                severity="secondary"
                outlined
                :label="t('loginView.signInWithCustomerOIDC')"
                @click="signInWithCustomerOIDC"
              />
              <i-button
                v-if="showKMHSupportButton"
                data-test="kmh-support-login-button"
                severity="secondary"
                outlined
                :label="t('loginView.signInAsKMHSupport')"
                @click="signInAsKMHSupport"
              />
            </div>
          </template>
        </authenticator>
      </div>
    </div>
    <!-- show skeleton of ForceNewPassword form while signIn form is processing -->
    <div
      v-if="isProcessingInitialLogin"
      data-test="initial-login-skeleton"
    >
      <div class="w-full py-8 px-40">
        <p-skeleton width="16rem" class="mb-24" />

        <p-skeleton width="28rem" height="2rem" class="mb-8" />

        <p-skeleton width="6rem" class="mb-2" />
        <p-skeleton width="100%" height="2.5rem" class="mb-6" />

        <p-skeleton width="12rem" class="mb-2" />
        <p-skeleton width="100%" height="2.5rem" class="mb-10" />

        <p-skeleton width="12rem" class="mb-4" />
        <p-skeleton width="14rem" class="mb-3" />
        <p-skeleton width="14rem" class="mb-3" />
        <p-skeleton width="14rem" class="mb-3" />
        <p-skeleton width="14rem" class="mb-8" />

        <p-skeleton width="100%" height="2.5rem" class="mb-8" />
        <div class="flex justify-center">
          <p-skeleton width="9rem" />
        </div>
      </div>
    </div>
  </i-login-wrapper>

  <div v-else-if="!isLoggedIn" class="size-full flex justify-center items-center">
    <div class="w-120">
      <img src="/src/assets/intellivend_logo.svg" alt="intellivend logo">

      <i-progress-bar />

      <div v-if="redirectingToSidebar" class="text-primary text-xl text-center mt-2">
        {{ t('loginView.redirectingToSidebar') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
/** Negate the reset header padding as it does not behave like the sign in header */
[data-amplify-authenticator-forgotpassword] img,
[data-amplify-authenticator-confirmresetpassword] img {
  margin: -1rem -2rem 1rem;
  width: calc(100% + 4rem);
}
</style>
